import { useContext } from 'react';

import { ForecastingDataContext } from '../context/forecasting-data-context';

/**
 * @typedef {import('../types').SkuVelocityDataItem} SkuVelocityDataItem
 * @typedef {import('../types').OrderVelocityData} OrderVelocityData
 * @typedef {import('../types').InventoryItemFromEntity} InventoryItemFromEntity
 * @typedef {import('../types').ForecastingDataItem} ForecastingDataItem
 */

/**
 * @typedef {object} ForecastingDataContextValue
 * @property {Array<ForecastingDataItem>} data - The forecasting data array
 * @property {boolean} isLoading - Loading state
 * @property {Error | null} error - Error object if any
 * @property {import('../types').QuantityValuesMap} quantityValues - User-modified quantity values (takes precedence over item.quantity when present)
 * @property {(rowId: string, value: number) => void} updateData - Function to update data
 * @property {OrderVelocityData} orderVelocityData - Order velocity data from API
 * @property {string} lookbackPeriod - Lookback period setting
 * @property {string} forecastPeriod - Forecast period setting
 * @property {number} salesTrend - Sales trend percentage
 * @property {number} leadTimeDays - Lead time in days
 * @property {number} bufferStockDays - Buffer stock in days
 * @property {Array<ForecastingDataItem>} forecastingData - Processed forecasting data
 * @property {Array<InventoryItemFromEntity>} rawEntities - Raw entity data
 */

/**
 * Hook to access the forecasting data context
 * @returns {ForecastingDataContextValue} The forecasting data context value
 */
export const useForecastingData = () => {
  const context = useContext(ForecastingDataContext);
  if (!context) {
    throw new Error('useForecastingData must be used within a ForecastingDataProvider');
  }
  return context;
};
